import { useEffect, useState } from 'react';

import { isUnauthenticatedError } from '@confluence/error-boundary/entry-points/isUnauthenticatedError.ts';
import { markErrorAsHandled } from '@confluence/graphql-error-processor/entry-points/GraphqlErrorProcessor.ts';
import { cfetch, RateLimitedError } from '@confluence/network';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

type ImpersonationDetailsType = {
	loading: boolean;
	isImpersonating: boolean;
	targetUserId: string | null;
};

export const useIsImpersonating = ({
	cloudId,
	onError,
}: {
	cloudId: string;
	onError: (error: Error) => void;
}) => {
	const { isLicensed } = useSessionData();
	const [impersonationDetails, setImpersonationDetails] = useState<ImpersonationDetailsType>({
		loading: true,
		isImpersonating: false,
		targetUserId: null,
	});
	useEffect(
		() => {
			const fetchImpersonationDetails = async () => {
				try {
					const response = await cfetch(`/gateway/api/impersonation?targetId=${cloudId}`);
					const json = await response.json();
					setImpersonationDetails({
						loading: false,
						isImpersonating: json.isImpersonating,
						targetUserId: json.isImpersonating ? json.targetUserId : null,
					});
				} catch (e) {
					// Handling unauthenticated errors if session data cache is stale.
					// Handling rate limited errors since test users will bloat monitoring
					// and an on-call engineer can't do anything about rate limiting.
					if (isUnauthenticatedError(e) || e instanceof RateLimitedError) {
						markErrorAsHandled(e);
					} else {
						onError(e);
					}
				}
			};
			if (isLicensed) void fetchImpersonationDetails();
		},
		// We only want to fetch the endpoint on initial mount and not any other time
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[],
	);

	return impersonationDetails;
};
